<template>
  <div class="fnc-detail-page">
    <div v-if="isLoading" class="fnc-detail-loading" aria-label="Chargement de la FNC" aria-busy="true">
      <section class="fnc-detail-hero fnc-detail-hero--skeleton">
        <div class="fnc-detail-hero-main">
          <div class="fnc-detail-skeleton-row">
            <span class="fnc-detail-skeleton fnc-detail-skeleton--badge"></span>
            <span class="fnc-detail-skeleton fnc-detail-skeleton--badge-small"></span>
          </div>
          <span class="fnc-detail-skeleton fnc-detail-skeleton--eyebrow"></span>
          <span class="fnc-detail-skeleton fnc-detail-skeleton--hero-title"></span>
          <span class="fnc-detail-skeleton fnc-detail-skeleton--hero-copy"></span>
        </div>
        <div class="fnc-detail-hero-dates">
          <span class="fnc-detail-skeleton fnc-detail-skeleton--date"></span>
          <span class="fnc-detail-skeleton fnc-detail-skeleton--date"></span>
        </div>
      </section>
      <div class="row fnc-detail-layout">
        <div class="col-lg-8">
          <section v-for="index in 2" :key="'main-' + index" class="fnc-detail-section fnc-detail-section--skeleton">
            <span class="fnc-detail-skeleton fnc-detail-skeleton--section-title"></span>
            <div class="fnc-detail-skeleton-grid">
              <span v-for="item in 6" :key="item" class="fnc-detail-skeleton fnc-detail-skeleton--field"></span>
            </div>
          </section>
        </div>
        <div class="col-lg-4">
          <section v-for="index in 2" :key="'side-' + index" class="fnc-detail-section fnc-detail-section--skeleton">
            <span class="fnc-detail-skeleton fnc-detail-skeleton--section-title"></span>
            <span v-for="item in 4" :key="item" class="fnc-detail-skeleton fnc-detail-skeleton--line"></span>
          </section>
        </div>
      </div>
    </div>

    <div v-else-if="loadError" class="fnc-detail-state">
      <span><i class="fas fa-exclamation-triangle"></i></span>
      <h2>Impossible de charger cette FNC</h2>
      <p>La fiche est indisponible ou une erreur est survenue.</p>
      <div>
        <button type="button" class="btn fnc-detail-secondary" @click="back"><i class="fas fa-arrow-left"></i> Retour à la liste</button>
        <button type="button" class="btn fnc-detail-primary" @click="fetchFnc"><i class="fas fa-sync-alt"></i> Réessayer</button>
      </div>
    </div>

    <template v-else>
      <div class="fnc-detail-topbar">
        <button type="button" class="fnc-detail-back" @click.stop="back">
          <i class="fas fa-arrow-left"></i>
          Retour aux FNC
        </button>
      </div>

      <section class="fnc-detail-hero">
        <div class="fnc-detail-hero-main">
          <div class="fnc-detail-badges">
            <span class="fnc-detail-status" :class="fnc.archived_at ? 'fnc-detail-status--closed' : 'fnc-detail-status--open'">
              <i class="fas" :class="fnc.archived_at ? 'fa-check-circle' : 'fa-clock'"></i>
              {{ fnc.archived_at ? 'Clôturée' : 'En cours' }}
            </span>
            <span v-if="fnc.importance" class="fnc-detail-importance" :class="fnc.importance === 'majeure' ? 'fnc-detail-importance--major' : ''">
              {{ capitalize(fnc.importance) }}
            </span>
          </div>
          <span class="fnc-detail-eyebrow">Fiche de non-conformité · {{ fnc.num }}</span>
          <h1>{{ productName }}</h1>
          <p>{{ supplierName }}<span v-if="productReference"> · Réf. {{ productReference }}</span></p>
        </div>
        <div class="fnc-detail-hero-dates">
          <div>
            <i class="fas fa-calendar-plus"></i>
            <span><small>Créée le</small><strong>{{ displayDate(fnc.created_at) }}</strong></span>
          </div>
          <div>
            <i class="fas" :class="fnc.archived_at ? 'fa-calendar-check' : 'fa-calendar-day'"></i>
            <span><small>{{ fnc.archived_at ? 'Clôturée le' : 'Date de réception' }}</small><strong>{{ displayDate(fnc.archived_at || fnc.received_at) }}</strong></span>
          </div>
        </div>
      </section>

      <div class="row fnc-detail-layout">
        <main class="col-lg-8">
          <section class="fnc-detail-section">
            <header class="fnc-detail-section-header">
              <span class="fnc-detail-section-icon"><i class="fas fa-clipboard-list"></i></span>
              <div><span>Informations</span><h2>Description de la non-conformité</h2></div>
            </header>

            <div class="fnc-detail-info-grid">
              <div class="fnc-detail-info-item">
                <small>Fournisseur</small>
                <strong>{{ supplierName }}</strong>
              </div>
              <div class="fnc-detail-info-item">
                <small>Produit</small>
                <strong>{{ productName }}</strong>
              </div>
              <div class="fnc-detail-info-item">
                <small>Référence</small>
                <strong>{{ valueOr(productReference) }}</strong>
              </div>
              <div class="fnc-detail-info-item">
                <small>Quantité</small>
                <strong>{{ valueOr(fnc.quantity) }}</strong>
              </div>
              <div class="fnc-detail-info-item">
                <small>Lieu</small>
                <strong>{{ valueOr(fnc.location) }}</strong>
              </div>
              <div class="fnc-detail-info-item">
                <small>Date de réception</small>
                <strong>{{ displayDate(fnc.received_at) }}</strong>
              </div>
              <div class="fnc-detail-info-item">
                <small>Responsable</small>
                <strong>{{ nestedName(fnc.controller) }}</strong>
              </div>
              <div class="fnc-detail-info-item">
                <small>Contact</small>
                <strong>{{ nestedName(fnc.contact) }}</strong>
              </div>
            </div>

            <div class="fnc-detail-text-grid">
              <article class="fnc-detail-text-card fnc-detail-text-card--accent">
                <span>Constat</span>
                <p>{{ valueOr(fnc.cause, 'Non renseignée') }}</p>
              </article>
              <article class="fnc-detail-text-card fnc-detail-text-card--wide">
                <span>Observation interne</span>
                <p>{{ valueOr(fnc.intern_observation, 'Non renseignée') }}</p>
              </article>
            </div>
          </section>

          <section class="fnc-detail-section">
            <header class="fnc-detail-section-header">
              <span class="fnc-detail-section-icon fnc-detail-section-icon--blue"><i class="fas fa-tasks"></i></span>
              <div><span>Suivi</span><h2>Actions mises en place</h2></div>
            </header>

            <div class="fnc-detail-text-grid">
              <article class="fnc-detail-text-card">
                <span>Cause (fournisseur)</span>
                <p>{{ valueOr(fnc.supplier_cause, 'Non renseignée') }}</p>
              </article>
              <article class="fnc-detail-text-card">
                <span>Date de validation (par le fournisseur)</span>
                <p>{{ displayDate(fnc.supplier_validation_date, 'Non renseignée') }}</p>
              </article>
            </div>
            <div class="fnc-action-timeline">
              <article class="fnc-action-item">
                <span class="fnc-action-marker"><i class="fas fa-bolt"></i></span>
                <div>
                  <div class="fnc-action-heading">
                    <h3>Action curative immédiate</h3>
                    <span>{{ displayDate(fnc.curative_action_date, 'Date non définie') }}</span>
                  </div>
                  <p>{{ valueOr(fnc.currative_action, 'Aucune action renseignée') }}</p>
                </div>
              </article>
              <article class="fnc-action-item">
                <span class="fnc-action-marker fnc-action-marker--blue"><i class="fas fa-wrench"></i></span>
                <div>
                  <div class="fnc-action-heading">
                    <h3>Actions correctives</h3>
                    <span>Échéance : {{ displayDate(fnc.time_limit, 'non définie') }}</span>
                  </div>
                  <p>{{ valueOr(fnc.corrective_action, 'Aucune action renseignée') }}</p>
                </div>
              </article>
            </div>
          </section>

          <section class="fnc-detail-section">
            <header class="fnc-detail-section-header">
              <span class="fnc-detail-section-icon fnc-detail-section-icon--purple"><i class="fas fa-paperclip"></i></span>
              <div><span>Documents</span><h2>Pièces jointes</h2></div>
              <span class="fnc-detail-file-count">{{ files.length }}</span>
            </header>

            <div v-if="files.length" class="fnc-files-grid">
              <button v-for="file in files" :key="file.id || file.name" type="button" class="fnc-file-card" @click="openImageInNewTab(file.base64)">
                <span class="fnc-file-icon" :class="fileIconClass(file)"><i :class="fileIcon(file)"></i></span>
                <span><strong :title="file.name">{{ file.name }}</strong><small>{{ fileExtension(file) }}</small></span>
                <i class="fas fa-external-link-alt fnc-file-open"></i>
              </button>
            </div>
            <div v-else class="fnc-detail-empty">
              <i class="fas fa-paperclip"></i>
              <span>Aucune pièce jointe pour cette FNC.</span>
            </div>
          </section>
        </main>

        <aside class="col-lg-4">
          <section class="fnc-detail-section fnc-detail-side-section">
            <header class="fnc-detail-section-header">
              <span class="fnc-detail-section-icon fnc-detail-section-icon--green"><i class="fas fa-user"></i></span>
              <div><span>Traçabilité</span><h2>Origine de la fiche</h2></div>
            </header>

            <div class="fnc-person-card">
              <span class="fnc-person-avatar">{{ initials(nestedName(fnc.creator)) }}</span>
              <div><small>Ouverte par</small><strong>{{ nestedName(fnc.creator) }}</strong><span>{{ nestedDepartment(fnc.creator) }}</span></div>
            </div>

            <div class="fnc-detail-divider"></div>

            <div v-if="fnc.detector" class="fnc-person-card fnc-person-card--secondary">
              <span class="fnc-person-avatar">{{ initials(nestedName(fnc.detector)) }}</span>
              <div><small>Détectée par</small><strong>{{ nestedName(fnc.detector) }}</strong><span>{{ nestedDepartment(fnc.detector) }}</span></div>
            </div>
            <div v-else class="fnc-detail-side-field">
              <small>Origine déclarée</small>
              <strong>{{ valueOr(fnc.origin) }}</strong>
            </div>
          </section>

          <section class="fnc-detail-section fnc-detail-side-section">
            <header class="fnc-detail-section-header">
              <span class="fnc-detail-section-icon fnc-detail-section-icon--green"><i class="fas fa-check-double"></i></span>
              <div><span>Bilan</span><h2>Clôture de la fiche</h2></div>
            </header>

            <div class="fnc-closure-grid">
              <div class="fnc-detail-side-field">
                <small>Efficacité</small>
                <strong>{{ valueOr(fnc.efficiency) }}</strong>
              </div>
              <div class="fnc-detail-side-field">
                <small>Coût</small>
                <strong>{{ formatCost(fnc.cost) }}</strong>
              </div>
              <div class="fnc-detail-side-field">
                <small>Visa responsable</small>
                <strong>{{ nestedName(fnc.controller) }}</strong>
              </div>
              <div class="fnc-detail-side-field">
                <small>Visa direction</small>
                <strong>{{ nestedName(fnc.director) }}</strong>
              </div>
              <div class="fnc-detail-side-field fnc-detail-side-field--wide">
                <small>Date de clôture</small>
                <strong>{{ displayDate(fnc.archived_at, 'Non clôturée') }}</strong>
              </div>
            </div>

            <div class="fnc-detail-comment">
              <i class="fas fa-quote-left"></i>
              <div><small>Commentaire</small><p>{{ valueOr(fnc.comments, 'Aucun commentaire') }}</p></div>
            </div>
          </section>
        </aside>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'Fnc',
  data() {
    return {
      fnc: {},
      isLoading: true,
      loadError: false
    }
  },
  computed: {
    productName() {
      return this.fnc.product && this.fnc.product.name ? this.fnc.product.name : 'Produit non spécifié'
    },
    productReference() {
      return this.fnc.product && this.fnc.product.reference ? this.fnc.product.reference : ''
    },
    supplierName() {
      return this.fnc.supplier && this.fnc.supplier.name ? this.fnc.supplier.name : 'Fournisseur non spécifié'
    },
    files() {
      return Array.isArray(this.fnc.files) ? this.fnc.files : []
    }
  },
  mounted() {
    this.fetchFnc()
  },
  methods: {
    fetchFnc() {
      const id = this.$route.params.id
      if (id === undefined) {
        this.isLoading = false
        this.loadError = true
        return
      }

      this.isLoading = true
      this.loadError = false
      this.axios.get(process.env.VUE_APP_API_BASE_URL + 'fnc/' + id).then(response => {
        this.fnc = response.data
      }).catch(() => {
        this.loadError = true
      }).then(() => {
        this.isLoading = false
      })
    },
    back() {
      this.$router.push({ name: 'fnc list' })
    },
    updateFnc() {
      this.$router.push({ name: 'fnc form', params: { id: this.fnc.id, type: 0 } })
    },
    valueOr(value, fallback) {
      return value !== null && value !== undefined && value !== '' ? value : (fallback || '—')
    },
    displayDate(date, fallback) {
      if (!date) return fallback || '—'
      const parsed = new Date(date)
      if (Number.isNaN(parsed.getTime())) return fallback || '—'
      return parsed.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
    },
    nestedName(person) {
      return person && person.name ? person.name : 'Non renseigné'
    },
    nestedDepartment(person) {
      return person && person.department && person.department.name ? person.department.name : 'Service non renseigné'
    },
    initials(name) {
      return String(name || '?').split(' ').filter(Boolean).slice(0, 2).map(part => part.charAt(0)).join('').toUpperCase()
    },
    capitalize(value) {
      const text = String(value || '')
      return text.charAt(0).toUpperCase() + text.slice(1)
    },
    formatCost(cost) {
      if (cost === null || cost === undefined || cost === '') return 'Non renseigné'
      return Number(cost).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })
    },
    fileExtension(file) {
      return String((file && file.extension) || 'fichier').replace('.', '').toUpperCase()
    },
    fileIcon(file) {
      const extension = this.fileExtension(file).toLowerCase()
      if (extension === 'pdf') return 'fas fa-file-pdf'
      if (['jpg', 'jpeg', 'png', 'svg', 'webp'].indexOf(extension) !== -1) return 'fas fa-file-image'
      if (['xls', 'xlsx', 'csv'].indexOf(extension) !== -1) return 'fas fa-file-excel'
      if (['doc', 'docx'].indexOf(extension) !== -1) return 'fas fa-file-word'
      return 'fas fa-file'
    },
    fileIconClass(file) {
      const extension = this.fileExtension(file).toLowerCase()
      if (extension === 'pdf') return 'fnc-file-icon--pdf'
      if (['jpg', 'jpeg', 'png', 'svg', 'webp'].indexOf(extension) !== -1) return 'fnc-file-icon--image'
      if (['xls', 'xlsx', 'csv'].indexOf(extension) !== -1) return 'fnc-file-icon--excel'
      if (['doc', 'docx'].indexOf(extension) !== -1) return 'fnc-file-icon--word'
      return ''
    },
    openImageInNewTab(base64) {
      if (!base64 || base64.indexOf(',') === -1) return

      const byteCharacters = atob(base64.split(',')[1])
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i)
      const byteArray = new Uint8Array(byteNumbers)
      const mimeType = base64.split(',')[0].split(':')[1].split(';')[0]
      const blobUrl = URL.createObjectURL(new Blob([byteArray], { type: mimeType }))
      window.open(blobUrl, '_blank', 'noopener')
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000)
    }
  }
}
</script>

<style>
.fnc-detail-page {
  --detail-primary: #f96332;
  --detail-ink: #233044;
  --detail-muted: #748096;
  --detail-border: #e5e9ef;
  --detail-label-size: 11px;
  --detail-meta-size: 12px;
  --detail-text-size: 14px;
  --detail-title-size: 18px;
  font-size: var(--detail-text-size);
  line-height: 1.5;
  padding-bottom: 24px;
}

.fnc-detail-topbar {
  align-items: center;
  display: flex;
  justify-content: end;
  margin-bottom: 14px;
  min-height: 40px;
}

.fnc-detail-back {
  align-items: center;
  background: transparent;
  border: 0;
  color: #66748a;
  cursor: pointer;
  display: inline-flex;
  font-size: 13px;
  font-weight: 600;
  gap: 8px;
  padding: 7px 0;
}

.fnc-detail-back:hover { color: var(--detail-primary); text-decoration: none; }
.fnc-detail-primary,
.fnc-detail-secondary {
  align-items: center;
  border-radius: 9px;
  display: inline-flex;
  font-size: 13px;
  font-weight: 600;
  gap: 8px;
  justify-content: center;
  margin: 0;
  min-height: 42px;
  padding: 10px 16px;
  text-transform: none;
}

.fnc-detail-primary {
  background: var(--detail-primary);
  border: 1px solid var(--detail-primary);
  box-shadow: 0 7px 16px rgba(249, 99, 50, .18);
  color: #fff;
}

.fnc-detail-primary:hover { background: #dc4c1d; border-color: #dc4c1d; color: #fff; }
.fnc-detail-secondary { background: #fff; border: 1px solid var(--detail-border); box-shadow: none; color: #59677c; }

.fnc-detail-hero {
  align-items: stretch;
  background: linear-gradient(120deg, #26364c, #344b68);
  border-radius: 14px;
  box-shadow: 0 12px 28px rgba(31, 44, 63, .16);
  color: #fff;
  display: flex;
  justify-content: space-between;
  margin-bottom: 18px;
  min-height: 190px;
  overflow: hidden;
  padding: 28px 30px;
  position: relative;
}

.fnc-detail-hero::after {
  background: radial-gradient(circle, rgba(255, 255, 255, .12) 0, rgba(255, 255, 255, 0) 67%);
  content: '';
  height: 270px;
  position: absolute;
  right: -65px;
  top: -120px;
  width: 270px;
}

.fnc-detail-hero-main { align-self: center; flex: 1 1 auto; min-width: 0; position: relative; z-index: 1; }
.fnc-detail-badges { align-items: center; display: flex; flex-wrap: wrap; gap: 7px; margin-bottom: 14px; }
.fnc-detail-status,
.fnc-detail-importance {
  align-items: center;
  border-radius: 20px;
  display: inline-flex;
  font-size: var(--detail-label-size);
  font-weight: 700;
  gap: 5px;
  padding: 6px 10px;
}

.fnc-detail-status--open { background: #fff2d5; color: #8b5a00; }
.fnc-detail-status--closed { background: #dff5e7; color: #247542; }
.fnc-detail-importance { background: rgba(255, 255, 255, .13); color: #e9eef5; }
.fnc-detail-importance--major { background: #ffe5e5; color: #b93333; }
.fnc-detail-eyebrow { color: #b8c4d4; display: block; font-size: var(--detail-label-size); font-weight: 600; letter-spacing: .08em; text-transform: uppercase; }
.fnc-detail-hero h1 { color: #fff; font-size: 30px; font-weight: 700; letter-spacing: -.02em; line-height: 1.2; margin: 7px 0 5px; }
.fnc-detail-hero-main > p { color: #ced7e3; font-size: var(--detail-text-size); line-height: 1.45; margin: 0; }

.fnc-detail-hero-dates {
  align-self: center;
  border-left: 1px solid rgba(255, 255, 255, .15);
  display: flex;
  flex: 0 0 255px;
  flex-direction: column;
  gap: 14px;
  margin-left: 25px;
  padding-left: 25px;
  position: relative;
  z-index: 1;
}

.fnc-detail-hero-dates > div { align-items: center; display: flex; gap: 11px; }
.fnc-detail-hero-dates i { color: #ffac90; font-size: 16px; text-align: center; width: 20px; }
.fnc-detail-hero-dates span { display: flex; flex-direction: column; }
.fnc-detail-hero-dates small { color: #b8c4d4; font-size: 10px; font-weight: 600; letter-spacing: .04em; margin-bottom: 2px; text-transform: uppercase; }
.fnc-detail-hero-dates strong { color: #fff; font-size: var(--detail-text-size); font-weight: 600; }

.fnc-detail-layout { margin-left: -7px; margin-right: -7px; }
.fnc-detail-layout > [class*='col-'] { padding-left: 7px; padding-right: 7px; }
.fnc-detail-section {
  background: #fff;
  border: 1px solid var(--detail-border);
  border-radius: 14px;
  box-shadow: 0 6px 18px rgba(35, 48, 68, .045);
  margin-bottom: 14px;
  padding: 24px;
}

.fnc-detail-section-header { align-items: center; display: flex; margin-bottom: 22px; }
.fnc-detail-section-icon {
  align-items: center;
  background: #fff0eb;
  border-radius: 9px;
  color: var(--detail-primary);
  display: inline-flex;
  flex: 0 0 42px;
  font-size: 15px;
  height: 42px;
  justify-content: center;
  margin-right: 11px;
}

.fnc-detail-section-icon--blue { background: #edf4ff; color: #4776c8; }
.fnc-detail-section-icon--green { background: #eaf8ef; color: #338153; }
.fnc-detail-section-icon--purple { background: #f2edff; color: #7255b7; }
.fnc-detail-section-header > div { min-width: 0; }
.fnc-detail-section-header > div > span { color: #8f9aab; display: block; font-size: var(--detail-label-size); font-weight: 700; letter-spacing: .07em; margin-bottom: 2px; text-transform: uppercase; }
.fnc-detail-section-header h2 { color: var(--detail-ink); font-size: var(--detail-title-size); font-weight: 700; line-height: 1.25; margin: 0; }

.fnc-detail-info-grid {
  border-bottom: 1px solid #edf0f4;
  display: grid;
  gap: 22px 20px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-bottom: 20px;
  padding-bottom: 20px;
}

.fnc-detail-info-item,
.fnc-detail-side-field { display: flex; flex-direction: column; min-width: 0; }
.fnc-detail-info-item small,
.fnc-detail-side-field small { color: #8f9aab; font-size: var(--detail-label-size); font-weight: 600; letter-spacing: .03em; line-height: 1.35; margin-bottom: 5px; text-transform: uppercase; }
.fnc-detail-info-item strong,
.fnc-detail-side-field strong { color: #3f4c60; font-size: var(--detail-text-size); font-weight: 600; line-height: 1.45; overflow-wrap: anywhere; }

.fnc-detail-text-grid { display: grid; gap: 10px; grid-template-columns: repeat(2, minmax(0, 1fr)); }
.fnc-detail-text-card {
  background: #f8f9fb;
  border-left: 3px solid #dce2e9;
  border-radius: 0 8px 8px 0;
  min-height: 104px;
  padding: 16px;
}

.fnc-detail-text-card--accent { border-left-color: #f3a388; }
.fnc-detail-text-card--wide { grid-column: 1 / -1; min-height: 86px; }
.fnc-detail-text-card span { color: #8995a7; font-size: var(--detail-label-size); font-weight: 700; letter-spacing: .05em; text-transform: uppercase; }
.fnc-detail-text-card p { color: #4e5c71; font-size: var(--detail-text-size); line-height: 1.6; margin: 7px 0 0; white-space: pre-line; }

.fnc-action-timeline { position: relative; }
.fnc-action-item { display: grid; gap: 15px; grid-template-columns: 40px minmax(0, 1fr); position: relative; }
.fnc-action-item:not(:last-child) { padding-bottom: 24px; }
.fnc-action-item:not(:last-child)::after { background: #e5e9ef; content: ''; height: calc(100% - 38px); left: 19px; position: absolute; top: 43px; width: 1px; }
.fnc-action-marker {
  align-items: center;
  background: #fff1ec;
  border: 1px solid #ffd6c8;
  border-radius: 50%;
  color: var(--detail-primary);
  display: inline-flex;
  font-size: 13px;
  height: 40px;
  justify-content: center;
  position: relative;
  width: 40px;
  z-index: 1;
}

.fnc-action-marker--blue { background: #edf4ff; border-color: #d7e5fb; color: #4776c8; }
.fnc-action-heading { align-items: center; display: flex; gap: 12px; justify-content: space-between; margin: 2px 0 8px; }
.fnc-action-heading h3 { color: #3f4c60; font-size: 15px; font-weight: 700; line-height: 1.35; margin: 0; }
.fnc-action-heading span { background: #f4f6f8; border-radius: 20px; color: #748095; font-size: var(--detail-label-size); padding: 5px 10px; white-space: nowrap; }
.fnc-action-item p { color: #5d6a7f; font-size: var(--detail-text-size); line-height: 1.6; margin: 0; white-space: pre-line; }

.fnc-detail-file-count { background: #f2f4f7; border-radius: 20px; color: #748095; font-size: var(--detail-label-size); font-weight: 600; margin-left: auto; padding: 5px 9px; }
.fnc-files-grid { display: grid; gap: 9px; grid-template-columns: repeat(2, minmax(0, 1fr)); }
.fnc-file-card {
  align-items: center;
  background: #fff;
  border: 1px solid #e6eaf0;
  border-radius: 9px;
  cursor: pointer;
  display: flex;
  gap: 12px;
  min-width: 0;
  padding: 12px;
  text-align: left;
  transition: .18s ease;
}

.fnc-file-card:hover { border-color: #f2b39e; box-shadow: 0 5px 12px rgba(35, 48, 68, .07); transform: translateY(-1px); }
.fnc-file-icon { align-items: center; background: #f1f3f6; border-radius: 8px; color: #758296; display: inline-flex; flex: 0 0 42px; font-size: 14px; height: 42px; justify-content: center; }
.fnc-file-icon--pdf { background: #fff0f0; color: #c74747; }
.fnc-file-icon--image { background: #f2edff; color: #7255b7; }
.fnc-file-icon--excel { background: #eaf8ef; color: #338153; }
.fnc-file-icon--word { background: #edf4ff; color: #4776c8; }
.fnc-file-card > span:nth-child(2) { display: flex; flex: 1; flex-direction: column; min-width: 0; }
.fnc-file-card strong { color: #465368; font-size: 13px; line-height: 1.4; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fnc-file-card small { color: #8f9aab; font-size: 10px; margin-top: 2px; }
.fnc-file-open { color: #9ca6b5; font-size: 11px; }
.fnc-detail-empty { align-items: center; background: #f8f9fb; border: 1px dashed #dfe4ea; border-radius: 8px; color: #7f8a9c; display: flex; font-size: 13px; gap: 9px; justify-content: center; min-height: 85px; }

.fnc-detail-side-section { padding: 22px; }
.fnc-person-card { align-items: center; display: flex; gap: 13px; }
.fnc-person-avatar {
  align-items: center;
  background: #edf3f9;
  border-radius: 50%;
  color: #5d7089;
  display: inline-flex;
  flex: 0 0 48px;
  font-size: 13px;
  font-weight: 700;
  height: 48px;
  justify-content: center;
}

.fnc-person-card > div { display: flex; flex-direction: column; min-width: 0; }
.fnc-person-card small { color: #8f9aab; font-size: var(--detail-label-size); font-weight: 600; letter-spacing: .03em; text-transform: uppercase; }
.fnc-person-card strong { color: #3f4c60; font-size: var(--detail-text-size); line-height: 1.4; margin: 2px 0; }
.fnc-person-card > div > span { color: #748095; font-size: var(--detail-meta-size); line-height: 1.4; }
.fnc-person-card--secondary .fnc-person-avatar { background: #f6f1ec; color: #9a7555; }
.fnc-detail-divider { background: #edf0f4; height: 1px; margin: 16px 0; }
.fnc-closure-grid { display: grid; gap: 20px 16px; grid-template-columns: repeat(2, minmax(0, 1fr)); }
.fnc-detail-side-field--wide { grid-column: 1 / -1; }
.fnc-detail-comment { align-items: flex-start; background: #f8f9fb; border-radius: 8px; color: #a4adba; display: flex; gap: 10px; margin-top: 20px; padding: 14px; }
.fnc-detail-comment > i { font-size: 12px; margin-top: 3px; }
.fnc-detail-comment div { min-width: 0; }
.fnc-detail-comment small { color: #8f9aab; display: block; font-size: var(--detail-label-size); font-weight: 600; letter-spacing: .03em; text-transform: uppercase; }
.fnc-detail-comment p { color: #566378; font-size: 13px; line-height: 1.55; margin: 4px 0 0; white-space: pre-line; }

.fnc-detail-state {
  align-items: center;
  background: #fff;
  border: 1px dashed #dce2e9;
  border-radius: 13px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 430px;
  padding: 35px;
  text-align: center;
}

.fnc-detail-state > span { align-items: center; background: #fff1ec; border-radius: 50%; color: var(--detail-primary); display: inline-flex; font-size: 21px; height: 60px; justify-content: center; margin-bottom: 14px; width: 60px; }
.fnc-detail-state h2 { color: var(--detail-ink); font-size: 18px; font-weight: 700; margin: 0 0 5px; }
.fnc-detail-state p { color: var(--detail-muted); font-size: 13px; margin: 0 0 18px; }
.fnc-detail-state > div { display: flex; gap: 9px; }

.fnc-detail-skeleton {
  background: #e9edf2;
  border-radius: 5px;
  display: block;
  overflow: hidden;
  position: relative;
}

.fnc-detail-skeleton::after {
  animation: fnc-detail-shimmer 1.5s infinite;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, .75), transparent);
  content: '';
  inset: 0;
  position: absolute;
  transform: translateX(-100%);
}

@keyframes fnc-detail-shimmer { 100% { transform: translateX(100%); } }
.fnc-detail-skeleton--back { height: 12px; width: 105px; }
.fnc-detail-skeleton--button { height: 42px; width: 132px; }
.fnc-detail-hero--skeleton { background: #f2f4f7; box-shadow: none; }
.fnc-detail-hero--skeleton::after { display: none; }
.fnc-detail-skeleton-row { display: flex; gap: 7px; margin-bottom: 17px; }
.fnc-detail-skeleton--badge { border-radius: 20px; height: 23px; width: 80px; }
.fnc-detail-skeleton--badge-small { border-radius: 20px; height: 23px; width: 58px; }
.fnc-detail-skeleton--eyebrow { height: 11px; width: 170px; }
.fnc-detail-skeleton--hero-title { height: 32px; margin-top: 10px; width: 330px; }
.fnc-detail-skeleton--hero-copy { height: 14px; margin-top: 9px; width: 220px; }
.fnc-detail-skeleton--date { height: 42px; width: 190px; }
.fnc-detail-section--skeleton { min-height: 240px; }
.fnc-detail-skeleton--section-title { height: 22px; margin-bottom: 28px; width: 210px; }
.fnc-detail-skeleton-grid { display: grid; gap: 18px 14px; grid-template-columns: repeat(3, 1fr); }
.fnc-detail-skeleton--field { height: 42px; }
.fnc-detail-skeleton--line { height: 40px; margin-bottom: 13px; }

@media (max-width: 991px) {
  .fnc-detail-hero { flex-direction: column; }
  .fnc-detail-hero-dates { border-left: 0; border-top: 1px solid rgba(255, 255, 255, .15); flex: auto; flex-direction: row; margin: 22px 0 0; padding: 20px 0 0; }
  .fnc-detail-info-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 575px) {
  .fnc-detail-topbar { gap: 12px; }
  .fnc-detail-back { font-size: 12px; }
  .fnc-detail-back i { font-size: 12px; }
  .fnc-detail-primary { padding: 9px 11px; }
  .fnc-detail-hero { padding: 21px 18px; }
  .fnc-detail-hero h1 { font-size: 22px; }
  .fnc-detail-hero-dates { align-items: flex-start; flex-direction: column; }
  .fnc-detail-info-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .fnc-detail-text-grid,
  .fnc-files-grid { grid-template-columns: 1fr; }
  .fnc-detail-text-card--wide { grid-column: auto; }
  .fnc-action-heading { align-items: flex-start; flex-direction: column; gap: 5px; }
  .fnc-detail-state > div { align-items: stretch; flex-direction: column; width: 100%; }
  .fnc-detail-skeleton--hero-title { max-width: 100%; }
}

@media (min-width: 992px) {
  .fnc-detail-topbar {
    margin-bottom: 8px;
    min-height: 32px;
  }

  .fnc-detail-back { padding: 4px 0; }

  .fnc-detail-hero {
    margin-bottom: 12px;
    min-height: 145px;
    padding: 18px 24px;
  }

  .fnc-detail-badges { margin-bottom: 8px; }
  .fnc-detail-hero h1 { margin: 4px 0 3px; }
  .fnc-detail-hero-dates { gap: 8px; }

  .fnc-detail-layout { margin-left: -6px; margin-right: -6px; }
  .fnc-detail-layout > [class*='col-'] { padding-left: 6px; padding-right: 6px; }
  .fnc-detail-layout > aside { display: flex; flex-direction: column; }
  .fnc-detail-layout > aside > .fnc-detail-section:last-child { flex: 1 1 auto; margin-bottom: 0; }

  .fnc-detail-section,
  .fnc-detail-side-section {
    margin-bottom: 12px;
    padding: 16px 18px;
  }

  .fnc-detail-section-header { margin-bottom: 14px; }
  .fnc-detail-section-icon {
    flex-basis: 38px;
    height: 38px;
  }

  .fnc-detail-info-grid {
    gap: 14px 16px;
    margin-bottom: 14px;
    padding-bottom: 14px;
  }

  .fnc-detail-text-card {
    min-height: 88px;
    padding: 12px 14px;
  }

  .fnc-detail-text-card--wide { min-height: 76px; }
  .fnc-action-item { gap: 12px; grid-template-columns: 36px minmax(0, 1fr); }
  .fnc-action-item:not(:last-child) { padding-bottom: 14px; }
  .fnc-action-item:not(:last-child)::after { height: calc(100% - 34px); left: 17px; top: 39px; }
  .fnc-action-marker { height: 36px; width: 36px; }
  .fnc-action-heading { margin-bottom: 5px; }

  .fnc-file-card { padding: 9px 10px; }
  .fnc-file-icon { flex-basis: 36px; height: 36px; }
  .fnc-detail-empty { min-height: 70px; }

  .fnc-person-avatar {
    flex-basis: 44px;
    height: 44px;
  }

  .fnc-detail-divider { margin: 12px 0; }
  .fnc-closure-grid { gap: 14px 12px; }
  .fnc-detail-comment { margin-top: 14px; padding: 11px 12px; }
}

@media (min-width: 1200px) {
  .fnc-detail-layout > main {
    align-content: start;
    display: grid;
    gap: 12px;
    grid-template-columns: minmax(0, 1.15fr) minmax(250px, .85fr);
  }

  .fnc-detail-layout > main > .fnc-detail-section { margin-bottom: 0; }
  .fnc-detail-layout > main > .fnc-detail-section:first-child { grid-column: 1 / -1; }
  .fnc-detail-text-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .fnc-detail-text-card--wide { grid-column: auto; min-height: 88px; }
  .fnc-files-grid { grid-template-columns: 1fr; }
}
</style>
